import {
  ADM_FORMAT,
  ADM_FORMAT_PREVIEW,
  ADM_FORMATS_TEMPLATES,
  FORMAT_OPTIONS,
} from "campaign-builder/domain/constants";

export const BEST_SELLER_FORMAT = ADM_FORMAT.POSTCARD_9x6;

export interface AdmFormatMeta {
  value: ADM_FORMAT;
  label: string; // e.g. 'Postcard 6" × 9"'
  sizeLabel: string; // e.g. '6.00 × 9.00 in'
  price: string; // '$' | '$$' | '$$$' | '$$$$'
  image?: string;
  template?: string;
  isBestSeller: boolean;
}

const isAdmFormat = (value: string): value is ADM_FORMAT => Object.values(ADM_FORMAT).includes(value as ADM_FORMAT);

// International formats are named and sized in metric; US postcards keep the
// inch-based derived labels below.
const METRIC_FORMAT_META: Partial<Record<ADM_FORMAT, { name: string; sizeLabel: string }>> = {
  [ADM_FORMAT.A6]: { name: "A6", sizeLabel: "14.8 × 10.5 cm" },
  [ADM_FORMAT.DL]: { name: "DL", sizeLabel: "21.0 × 9.9 cm" },
  [ADM_FORMAT.A5]: { name: "A5", sizeLabel: "21.0 × 14.8 cm" },
  [ADM_FORMAT.A4]: { name: "A4", sizeLabel: "21.0 × 29.7 cm" },
};

export const getAdmFormatMeta = (value?: string | null): AdmFormatMeta | null => {
  if (!value || !isAdmFormat(value)) return null;
  const preview = ADM_FORMAT_PREVIEW[value];
  const option = FORMAT_OPTIONS.find((o) => o.value === value);
  const metric = METRIC_FORMAT_META[value];
  const height = preview.heightInInch;
  const width = preview.widthInInch;
  return {
    value,
    label: metric ? `${metric.name} postcard` : `Postcard ${height}" × ${width}"`,
    sizeLabel: metric ? metric.sizeLabel : `${height.toFixed(2)} × ${width.toFixed(2)} in`,
    price: option?.bottomText ?? "",
    image: option?.image,
    template: ADM_FORMATS_TEMPLATES[value as keyof typeof ADM_FORMATS_TEMPLATES],
    isBestSeller: value === BEST_SELLER_FORMAT,
  };
};

export const getAdmFormatList = (allowed?: string[]): AdmFormatMeta[] => {
  // Fail closed: an empty allow-list means the country sells no format, never "every format".
  const values = allowed ?? Object.values(ADM_FORMAT);
  return values.map((value) => getAdmFormatMeta(value)).filter((meta): meta is AdmFormatMeta => meta !== null);
};
